import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, PencilRuler, Sprout, Scissors, Construction, Droplets, TreePine, Flower2, Leaf } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import api, { fileUrl } from "@/lib/api";
import Seo from "@/components/Seo";

const ICONS = { ClipboardCheck, PencilRuler, Sprout, Scissors, Construction, Droplets, TreePine, Flower2 };

export default function Prestations() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);

  return (
    <div className="bg-cream pt-32 md:pt-40 pb-24">
      <Seo title="Nos prestations paysagères" path="/prestations" description="Découvrez toutes les prestations d'Acanthe Jardins : étude & diagnostics, conception, création et entretien d'espaces verts, terrassement, bassins & irrigation dans les Bouches-du-Rhône, le Var et le Vaucluse." />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl mb-20">
          <Overline>Nos prestations</Overline>
          <h1 className="mt-4 font-display text-5xl lg:text-6xl text-forest leading-[1.02]">Un accompagnement complet, de l'étude à l'entretien</h1>
          <p className="mt-6 text-lg text-stone leading-relaxed">
            Acanthe Jardins maîtrise l'ensemble des métiers de l'aménagement extérieur pour vous offrir un interlocuteur unique, du premier diagnostic jusqu'à l'entretien de vos espaces.
          </p>
        </Reveal>

        <div className="space-y-20 md:space-y-28">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Leaf;
            const flip = i % 2 === 1;
            return (
              <Reveal key={s.id}>
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                  {/* Visual */}
                  <div className={`relative ${flip ? "lg:order-last" : ""}`}>
                    <div className="relative rounded-[28px] overflow-hidden aspect-[4/3] shadow-xl shadow-forest/5">
                      <img src={fileUrl(s.image)} alt={s.title} className="absolute inset-0 h-full w-full object-cover" />
                      <span className="absolute top-5 left-5 h-12 w-12 rounded-full bg-cream/95 flex items-center justify-center"><Icon className="text-brand" size={22} /></span>
                    </div>
                    <span className={`pointer-events-none absolute -z-0 font-display text-[8rem] leading-none text-forest/[0.06] ${flip ? "-right-2 -top-14" : "-left-2 -top-14"}`}>0{i + 1}</span>
                  </div>

                  {/* Text */}
                  <div className={flip ? "lg:pr-6" : "lg:pl-6"}>
                    <span className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-terracotta">
                      <span className="h-px w-8 bg-terracotta" /> Prestation 0{i + 1}
                    </span>
                    <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest leading-[1.05]">{s.title}</h2>
                    <p className="mt-3 text-brand font-medium">{s.short}</p>
                    <p className="mt-4 text-stone leading-relaxed">{s.description}</p>
                    <Link to={`/prestations/${s.slug}`} data-testid={`prestation-${s.slug}`}
                      className="mt-7 inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 font-semibold text-cream hover:bg-brand transition-colors group">
                      Découvrir cette prestation <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* CTA band */}
        <Reveal className="mt-24">
          <div className="relative overflow-hidden rounded-[28px] bg-forest px-8 py-14 md:px-16 md:py-16 text-center">
            <h2 className="font-display text-3xl md:text-5xl text-cream">Un projet en tête ?</h2>
            <p className="mt-4 text-cream/70 max-w-xl mx-auto">Parlons-en. Nos experts réalisent une étude personnalisée et un devis gratuit sous 24h.</p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 font-semibold text-cream hover:bg-cream hover:text-forest transition-colors">
              Demander un devis gratuit <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
